import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useProducts } from "../context/ProductContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";

const Navbar = ({ onSearch, onClearSearch }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const navigate = useNavigate();
  const { products, categories } = useProducts();
  const { getCartCount } = useCart();
  const { wishlistCount } = useWishlist();

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim().length > 0) {
      const filtered = products.filter(
        (p) =>
          p.name.toLowerCase().includes(value.toLowerCase()) ||
          p.description.toLowerCase().includes(value.toLowerCase()),
      );
      setSearchResults(filtered.slice(0, 6));
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      onSearch(searchTerm);
      navigate("/");
      setShowSearchResults(false);
    }
  };

  const handleProductClick = (productId) => {
    setSearchTerm("");
    setShowSearchResults(false);
    navigate(`/product/${productId}`);
  };

  const getCategoryName = (categoryId) => {
    return categories.find((c) => c.id === categoryId)?.name || "";
  };

  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link
            to="/"
            onClick={onClearSearch}
            className="flex items-center space-x-2"
          >
            <img
              src="/logo.png"
              alt="UyirUdalFoods Logo"
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-bold text-green-600">
              UyirUdalFoods
            </span>
          </Link>

          <div
            className="hidden md:flex flex-1 max-w-md mx-8 relative"
            ref={searchRef}
          >
            <form onSubmit={handleSearch} className="w-full flex">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearchInput}
                className="w-full px-4 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                type="submit"
                className="bg-green-500 text-white px-6 py-2 rounded-r-lg hover:bg-green-600"
              >
                🔍
              </button>
            </form>

            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-white shadow-xl rounded-lg py-2 max-h-96 overflow-y-auto z-50">
                {searchResults.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => handleProductClick(product.id)}
                    className="flex items-center px-4 py-3 hover:bg-green-50 cursor-pointer border-b last:border-b-0"
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                    <div className="ml-3 flex-1">
                      <p className="font-semibold text-gray-800 text-sm">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {getCategoryName(product.category)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">
                        ₹{product.offerPrice}
                      </p>
                      {product.price !== product.offerPrice && (
                        <p className="text-xs text-gray-500 line-through">
                          ₹{product.price}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showSearchResults &&
              searchResults.length === 0 &&
              searchTerm.trim() && (
                <div className="absolute top-full mt-2 w-full bg-white shadow-xl rounded-lg py-4 px-4 z-50">
                  <p className="text-gray-600 text-center">No products found</p>
                </div>
              )}
          </div>

          <div className="hidden md:flex items-center space-x-6">
            <Link
              to="/"
              onClick={onClearSearch}
              className="text-gray-700 hover:text-green-600 font-medium"
            >
              Home
            </Link>
            <Link
              to="/products"
              className="text-gray-700 hover:text-green-600 font-medium"
            >
              All Products
            </Link>
            <Link
              to="/categories"
              className="text-gray-700 hover:text-green-600 font-medium"
            >
              Categories
            </Link>
            <Link
              to="/wishlist"
              className="text-gray-700 hover:text-green-600 font-medium relative"
            >
              ❤️ Wishlist
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center badge-pulse">
                  {wishlistCount}
                </span>
              )}
            </Link>
            <Link
              to="/cart"
              className="text-gray-700 hover:text-green-600 font-medium relative"
            >
              🛒 Cart
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center badge-pulse">
                  {getCartCount()}
                </span>
              )}
            </Link>
            <Link
              to="/account"
              className="text-gray-700 hover:text-green-600 p-2 rounded-full hover:bg-gray-100 transition"
              title="Account Details"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </Link>
          </div>
        </div>

        <div className="md:hidden pb-3 relative" ref={mobileSearchRef}>
          <form onSubmit={handleSearch}>
            <div className="flex">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearchInput}
                className="w-full px-4 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                type="submit"
                className="bg-green-500 text-white px-4 py-2 rounded-r-lg"
              >
                🔍
              </button>
            </div>
          </form>

          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white shadow-xl rounded-lg py-2 max-h-80 overflow-y-auto z-[100]">
              {searchResults.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductClick(product.id)}
                  className="flex items-center px-4 py-3 hover:bg-green-50 active:bg-green-100 cursor-pointer border-b last:border-b-0"
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                  <div className="ml-3 flex-1">
                    <p className="font-semibold text-gray-800 text-sm">
                      {product.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {getCategoryName(product.category)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600 text-sm">
                      ₹{product.offerPrice}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
